<template>
    <div class="sfc-student-loan-calc">
        <div class="container-fluid wave">
			<div class="sfc-container">
				<h5>How much should you expect to pay on your student loans?</h5>
				<h1>Loan Calculator</h1>
				<!--Schema Markup FAQs-->
				<script v-html="create_schema(FAQList)" type="application/ld+json"/>
				<p class="mt-3"> This loan calculator can calculate the monthly loan payments on federal and private student loans, as well as mortgages and auto loans. To use the calculator, enter the total student loan amount, your current average interest rate and the repayment term in years. This calculator can calculate monthly payments under standard repayment and extended repayment for federal student loans. </p>
			</div>
			<div class="sfc-container" v-if="!resultDisplayed">
				<!-- this is the empty state of the loan calculator BEGIN -->
				<div class="sfc-card">
					<h4>
						<div class="social-media-area-floated mt-4">
							<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Loan Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/loan-calculator`" ></sfc-share-component>
						</div>
					</h4>
					<form @submit.prevent="recalculate" class="form sfc-col-12 content">
						<h4 class="cardTitle">
							Calculate your student loan payment below.
						</h4>
						<div class="input-group sfc-col-12">
							<label for="loan-amount" class="label"
								>Loan Amount</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="100"
									step="1"
									max="100000000000"
									v-model="input.loan_amount"
									name="loan-amount"
									id="loan-amount"
									required
								/>
								<span class="end">$</span>
								<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>The amount you <a href="/article/the-complete-guide-to-applying-for-student-loans" target="_blank">plan to borrow</a>. An estimate is just fine!</span
									>
								</div>
							</div>
						</div>
						<div class="input-group sfc-col-12">
							<label for="min-monthly-payment" class="label"
								>Minimum Payment</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="0"
									step="any"
									max="10000000"
									v-model="input.minMonthlyPayment"
									name="min-monthly-payment"
									id="min-monthly-payment"
									required
								/>
								<span class="end">$</span>
								<div class="tooltip">
										<img
											:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
											alt="info icon vector"
											class="info"
										/>
										<span class="tooltiptext"
											>Is your lender going to require you to pay a certain amount monthly? If not, enter 0.</span
										>
									</div>
							</div>
						</div>
						<div class="input-group sfc-col-12">
							<label for="avg-interest" class="label"
								>Average Interest Rate</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="0.1"
									step="any"
									max="100"
									v-model="input.avg_interest"
									name="avg-interest"
									id="avg-interest"
									required
								/>
								<span class="end">%</span>
								<div class="tooltip">
										<img
											:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
											alt="info icon vector"
											class="info"
										/>
										<span class="tooltiptext"
											>The <a href="/article/how-student-loan-interest-works" target="_blank">interest rate on the loan</a> you plan to take out, or the average interest rate across the multiple loans you plan to take out. Not sure what rate you’ll get? Try a standard 5% for starters and play what-if games with different rates.</span
										>
									</div>
							</div>
						</div>
						<div class="input-group sfc-col-12">
							<label for="loan-term" class="label"
								>Loan Term</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="1"
									max="50"
									step="1"
									v-model="input.loan_term"
									name="loan-term"
									id="loan-term"
									required
								/>
								<span class="end">YEARS</span>
								<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>The number of years you will take to <a href="/article/how-long-does-it-take-to-repay-a-student-loan" target="_blank">pay off what you borrowed</a>. Standard repayment is 10 years.</span
									>
								</div>
							</div>
						</div>
						<div class="input-group sfc-col-12 padding-side">
							<p class="text-left m-0 mt-4">
								<span class="gray-text"
									>Factor in the cost of fees?</span
								>
								<span>
									<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>Some lenders will charge you a fixed percentage of the loan, in addition to the interest rate. Check out this <a href="/best-private-student-loans-for-college" target="_blank">list</a> to see if yours does.</span
									>
									</div>
								</span>
							</p>
							<div class="sfc-col-6 flex checkbox-group">
								<div class="input-group">
									<input
										type="radio"
										class="custom-checkbox"
										id="question-1-yes"
										name="question-1"
										value="Yes"
										v-model="input.question_1"
									/>
									<label for="question-1-yes">Yes</label>
								</div>
								<div class="input-group ml-md-5">
									<input
										type="radio"
										class="custom-checkbox"
										id="question-1-no"
										name="question-1"
										value="No"
										v-model="input.question_1"
									/>
									<label for="question-1-no">No</label>
								</div>
							</div>
						</div>
						<div v-if="input.question_1=='Yes'" class="input-group sfc-col-12">
							<label for="fees-percentage" class="label mt-0"
								>Fees Pecentage</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="0"
									max="100"
									step="any"
									v-model="input.fees_percentage"
									name="loan-percentage"
									id="loan-percentage"
									required
								/>
								<span class="end">%</span>
								<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>You’ll only know this number if you have a particular lender in mind. Many lenders don’t charge any fees. Check out this <a href="/best-private-student-loans-for-college">list</a> to see if yours does.</span
									>
								</div>
							</div>
						</div>
						<button class="btn btn-primary">Calculate</button>
					</form>
				</div>
			</div>
			<div class="container" v-else>
				<div class="sfc-card completed">
					<div class="sfc-row">
						<form @submit.prevent="recalculate" class="form sfc-col-6 content text-center">
							<div class="input-group sfc-col-12">
								<h4>
									<div class="social-media-area-floated mt-4">
										<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Loan Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/loan-calculator`" ></sfc-share-component>
									</div>
								</h4>
							</div>
							<h4 class="cardTitle">
								Calculate your student loan payment below.
							</h4>
							<div class="input-group sfc-col-12">
								<label for="loan-amount" class="label"
									>Loan Amount</label
								><br />
								<div class="inner">
									<input
										placeholder=""
										class="input postend"
										type="number"
										min="100"
										max="100000000000"
										step="1"
										v-model="input.loan_amount"
										name="loan-amount"
										id="loan-amount"
										required
									/>
									<span class="end">$</span>
									<div class="tooltip">
										<img
											:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
											alt="info icon vector"
											class="info"
										/>
										<span class="tooltiptext"
											>The amount you <a href="/article/the-complete-guide-to-applying-for-student-loans" target="_blank">plan to borrow</a>. An estimate is just fine!</span
										>
									</div>
								</div>
							</div>
							<div class="input-group sfc-col-12">
								<label for="min-monthly-payment" class="label"
									>Minimum Payment</label
								><br />
								<div class="inner">
									<input
										placeholder=""
										class="input postend"
										type="number"
										min="0"
										step="any"
										max="10000000"
										v-model="input.minMonthlyPayment"
										name="min-monthly-payment"
										id="min-monthly-payment"
										required
									/>
									<span class="end">%</span>
									<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>What is the minimum monthly payment? If none, enter 0.</span
											>
										</div>
								</div>
							</div>
							<div class="input-group sfc-col-12">
								<label for="avg-interest" class="label"
									>Average Interest Rate</label
								><br />
								<div class="inner">
									<input
										placeholder=""
										class="input postend"
										type="number"
										min="0.1"
										step="any"
										max="100"
										v-model="input.avg_interest"
										name="avg-interest"
										id="avg-interest"
										required
									/>
									<span class="end">%</span>
									<div class="tooltip">
										<img
											:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
											alt="info icon vector"
											class="info"
										/>
										<span class="tooltiptext"
											>The <a href="/article/how-student-loan-interest-works" target="_blank">interest rate on the loan</a> you plan to take out, or the average interest rate across the multiple loans you plan to take out. Not sure what rate you’ll get? Try a standard 5% for starters and play what-if games with different rates.</span
										>
									</div>
								</div>
							</div>
							<div class="input-group sfc-col-12">
								<label for="loan-term" class="label"
									>Loan Term</label
								><br />
								<div class="inner">
									<input
										placeholder=""
										class="input postend"
										type="number"
										min="1"
										max="50"
										step="1"
										v-model="input.loan_term"
										name="loan-term"
										id="loan-term"
										required
									/>
									<span class="end">YEARS</span>
									<div class="tooltip">
										<img
											:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
											alt="info icon vector"
											class="info"
										/>
										<span class="tooltiptext"
											>The number of years you will take to <a href="/article/how-long-does-it-take-to-repay-a-student-loan" target="_blank">pay off what you borrowed</a>. Standard repayment is 10 years.</span
										>
									</div>
								</div>
							</div>
							<div class="input-group sfc-col-12 padding-side">
							<p class="text-left m-0 mt-4">
								<span class="gray-text"
									>Factor in the cost of fees?</span
								>
								<span>
									<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>Some lenders will charge you a fixed percentage of the loan, in addition to the interest rate. Check out this <a href="/best-private-student-loans-for-college" target="_blank">list</a> to see if yours does.</span
									>
									</div>
								</span>
							</p>
							<div class="sfc-col-6 flex checkbox-group">
								<div class="input-group">
									<input
										type="radio"
										class="custom-checkbox"
										id="question-1-yes"
										name="question-1"
										value="Yes"
										v-model="input.question_1"
									/>
									<label for="question-1-yes">Yes</label>
								</div>
								<div class="input-group ml-md-5">
									<input
										type="radio"
										class="custom-checkbox"
										id="question-1-no"
										name="question-1"
										value="No"
										v-model="input.question_1"
									/>
									<label for="question-1-no">No</label>
								</div>
							</div>
						</div>
						<div v-if="input.question_1=='Yes'" class="input-group sfc-col-12">
							<label for="fees-percentage" class="label mt-0"
								>Fees Pecentage</label
							><br />
							<div class="inner">
								<input
									placeholder=""
									class="input postend"
									type="number"
									min="0"
									max="100"
									step="any"
									v-model="input.fees_percentage"
									name="loan-percentage"
									id="loan-percentage"
									required
								/>
								<span class="end">%</span>
								<div class="tooltip">
									<img
										:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
										alt="info icon vector"
										class="info"
									/>
									<span class="tooltiptext"
										>You’ll only know this number if you have a particular lender in mind. Many lenders don’t charge any fees. Check out this <a href="/best-private-student-loans-for-college">list</a> to see if yours does.</span
									>
								</div>
							</div>
						</div>

							<button class="btn btn-primary">Calculate</button>
						</form>
						<div id="res" class="results bluebg sfc-col-6">
							<div class="content" style="height:55%">
								<h2>Your Results</h2>
								<p>Your Estimated Monthly Payment</p>
								<h1 class="largeText">${{formatNumber(monthlyPayment)}}</h1>
								<p class="small">Number of Payments</p>
								<h4>{{numberOfPayments}} Payments <span class="subText">({{durationString}})</span></h4>
							</div>
							<div class="graybg content text-center" style="height:45%">
								<div class="flex">
									<div class="sfc-col-6">
										<p>Total Payments</p>
										<h4 class="largeText">${{formatNumber(totalPayments)}}</h4>
									</div>
									<div class="sfc-col-6">
										<p>Total Interest Paid</p>
										<h4 class="largeText">${{formatNumber(totalInterestPaidWithFees)}}</h4>
									</div>
								</div>
								<div class="sfc-col-12">
									<br />
									<p class="medium">
										You can get a private student loan with
										a bank, credit union or online lender.
									</p>
									<a href="/best-private-student-loans-for-college" class="btn btn-secondary">
										Compare Private Loans
									</a>
								</div>
							</div>
							<div class="graybg content next-steps">
								<div class="sfc-col-12">
									<script defer src='https://sfc-external-widgets.savingforcollege.com/student_loan_widget_v2/production/loaders/loader.js?loan_type=originator&audience=undergraduate'></script>
								</div>
								<div class="sfc-col-12">
									<h4 class="cardTitle ml-0 pl-0">Next Steps:</h4>
									<ul>
										<li>Check out our <a href="/calculators/student-loan-payoff-calculator" target="_blank">other loan payoff calculator</a>. It shows you how much you can save and how much sooner you can pay off your loans by making extra payments. It includes a nifty graphical display of the progress in paying down your debt, plus a detailed payment schedule.</li>
										<li class="mt-2">Learn <a href="/article/how-long-does-it-take-to-repay-a-student-loan" target="_blank">Learn how long it takes to repay a student loan</a>.</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div class="sfc-row">
						<div class="sfc-col-12 content border-top text-center">
							<p>
								Be sure to first exhaust all federal loan options before seeking a private student loan
							</p>
							<a class="btn btn-tertiary" href="https://studentaid.gov/h/apply-for-aid/fafsa" target="_blank">
								Start the FAFSA
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
		  <!-- Site footer -->
		<footer class="site-footer" v-if="!blankLayout">
			<div style="margin:0px auto!important;padding:0px 15px;">
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-6 offset-lg-1 col-xl-5 offset-xl-2 content">
						<h6 class="footer_title">FAQs about loan calculator</h6>
						<div class="accordion" v-for="index in FAQList" :key="index">
							<div>
								<input type="radio" @click="toggleAccordion(index.id)" name="example_accordion" :id="index.id" class="student-loan-radio accordion__input">
								<label :for="index.id" class="accordion__label">{{ index.question }}</label>
								<div class="accordion__content">
								    <p v-html="index.answer"></p>
								</div>
							</div>
						</div>
					</div>
					<more-calculators></more-calculators>
				</div>
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2 content">
						<h6 class="footer_title">Related Content</h6>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20190122135939_percent.png`" alt="How Student Loan Interest Works" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/how-student-loan-interest-works" target="_blank">How Student Loan Interest Works</a>
								</p>
								<p class="card__detail">
									{{ `Interest is a fee charged by a lender for the use of borrowed money. For example, student loan borrowers are charged interest on the money received from education lenders. However, many student loan borrowers do not understand how interest works.` | capText}}
								</p>
							</div>
						</article>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://s3-us-west-2.amazonaws.com/webresources.savingforcollege.com/images/articles/facebook_20180207170637_does-a-529-plan-affect-financial-aid_4.52.56_pm.jpg`" alt="How Much Does a Student Loan Cost?" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/how-much-does-a-student-loan-cost" target="_blank">How Much Does a Student Loan Cost?</a>
								</p>
								<p class="card__detail">
									{{ `The cost of a student loan depends on more than just the interest rate. The real cost of a student loan depends on the cash flow characteristics of the student loan. The cash flow characteristics include how much money is received by the borrower and how much the borrower pays and how quickly.` | capText}}
								</p>
							</div>
						</article>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20190118163650_stock-21.jpg`" alt="How Long Does It Take to Repay a Student Loan?"/>
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/how-long-does-it-take-to-repay-a-student-loan" target="_blank">How Long Does It Take to Repay a Student Loan?</a>
								</p>
								<p class="card__detail">
									{{ `College graduates worry that they will be stuck repaying their student loans for decades, even during retirement. The amount of time it takes to repay a student loan in full depends on the type of loan, the amount borrowed, the interest rate and the repayment plan the borrower selects, as well as the use of deferments and forbearances. Borrowers typically take no more than 16-19 years to repay their federal student loans.` | capText}}
								</p>
							</div>
						</article>
					</div>
				</div>
			</div>
		</footer>
    </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { LoanCalculator } from 'sfc_calcs_js'
import sfcShareComponent from '@/common/components/share-component'
import MoreCalculators from '@/common/components/calculators/more-calculators'

export default {
	layout: 'default_student_loan',
	components: {
		sfcShareComponent,
		MoreCalculators
	},
    data(){
		let blankLayout = false
		if(this.$route.query.layout && this.$route.query.layout == "blank"){
			blankLayout = true
		}

		return {
			blankLayout,
			input: {
				loan_amount: null,
				fees_percentage: null,
				avg_interest: null,
				loan_term: null,
				minMonthlyPayment: 50,
				question_1:"No"
			},
			monthlyPayment:0,
			numberOfPayments:0,
			durationString:"",
			totalPayments:0,
			totalInterestPaid:0,
			resultDisplayed:false,
			feesAmount:0,
			totalInterestPaidWithFees:0,
			lastSectionOpened: "",
			FAQList:[
                {id: 1,
                question: "How do I know what my average interest rate is?",
                answer: "The interest rate on a student loan is specified in the loan promissory note or in your personal loan portal on the lender’s web site. If you have two or more student loans, each student loan could have a different interest rate.<br/> To determine the average interest rate, you will have to calculate the weighted average of the interest rates on the student loans. Multiply each loan’s interest rate by the loan balance, add these products together, and divide the result by the total of the loan balances."},
                {id: 2,
                question: "I’m not sure what exactly I owe. How do I find out?",
                answer: "For many college students, they are borrowing multiple student loans each year. It’s understandable to lose track along the way. To find your federal student loans, you can visit the <a href='https://studentaid.gov/' target='_blank'> U.S. Department of Education’s website</a> to locate your federal loans<br/> Finding private student loans is a little trickier. Look back at any previous paperwork or emails you may have received. You can also get a copy of your free credit report at <a href='https://www.annualcreditreport.com/foreignLocation.action' target='_blank'> annualcreditreport.com</a> to see which loans are listed. Both federal and private student loans are required to report their student loans to each of the three major credit bureaus. Confirm your current loan balance with each lender."},
                {id: 3,
                question: "How can I lower my loan payments?",
                answer: "There are several options to <a href='https://www.savingforcollege.com/article/how-to-lower-your-student-loan-payments' target='_blank'> reduce your monthly loan payment</a>. If you have federal student loans, you could apply for one of the <a href='https://www.savingforcollege.com/article/income-driven-repayment' target='_blank'>Income-Driven Repayment</a> plans, which bases your loan payment on your income and family size, as opposed to the amount you owe. Federal student loans are also eligible for a Graduated Repayment Plan (which increases the loan payments every two years) or an <a href='https://www.savingforcollege.com/article/extended-repayment' target='_blank'> Extended Repayment Plan </a> (which reduces the loan payment by choosing a longer repayment term). For private loans, call your lender directly to learn about the options they offer for payment reduction. You could also consider refinancing student loans into a new loan with a longer repayment term, thus lowering your monthly payment."},
                {id: 4,
                question: "How can I lower my interest rate?",
                answer: "Many student loan lenders offer a 0.25% interest rate reduction if you enroll in automatic payments. If you have good credit, you can consider refinancing student loans to get a potentially lower interest rate. Having a cosigner with good credit as well could help you score an even better interest rate."},
                {id: 5,
                question: "What do I do if I can’t make payments?",
                answer: "Call the lender to explore your options before you are late with a payment. You can apply for a deferment or forbearance, which temporarily pauses payments. The interest won’t accrue on subsidized federal student loans during a deferment, but it will accrue on unsubsidized loans and private loans. Both deferments and forbearances have eligibility requirements. Not all private lenders offer deferments and forbearances. Some private lenders offer a partial forbearance with interest-only loan payments."},
                {id: 6,
                question: "How long will it take to pay off my student loans?",
                answer: "The time it takes to pay off your student loan depends on many factors – your total balance, the interest rate, the repayment plan, the monthly payment and if you are able to pay extra towards your loans."},
                {id: 7,
                question: "How can I pay off my student loans faster?",
                answer: "The best way to pay off your student loans faster is to simply pay more than the required minimum payment each month. You can do this by cutting expenses to apply that money towards your debt. You can also find ways to apply extra money – getting a raise at work, working a side job or selling unwanted items for cash. Another option to consider is refinancing student loans, since a potential lower interest rate could help get rid of student loans faster."},
                {id: 8,
                question: "Can I make extra payments on my loans?",
                answer: "Yes, you can make extra payments on your student loans. There are no prepayment penalties on federal and private student loans. But you should contact your specific lender to see how they handle extra payments. You may need to specify which loan you would like your payment applied to, such as the loan with the highest interest rate. Otherwise, the lender may treat your extra payment as an early payment of the next installment. If you’re working towards Public Service Loan Forgiveness, it doesn’t make sense to make extra payments, since an extra payment will reduce the amount of your forgiveness."},
				{id: 9,
                question: "What are other options to help reduce my student loans?",
                answer: "Enroll in auto-payments for an interest rate reduction. Some private student loans provide a graduation reward, good grades reward or an on-time payment reward. For federal loans, you may be able to qualify for student loan forgiveness depending on your job. Consider unconventional options – move to an area that will help pay student loans, find a job that offers student loan repayment assistance or volunteer for potential loan forgiveness."},
            	{id: 10,
                question: "How can I reduce what I need to borrow?",
                answer: "If you are considering student loans to pay for college or graduate school, there are many ways to borrow responsibly. Fill out the Free Application for Federal Student Aid (FAFSA) to see what grants and federal aid you qualify for. Apply for as many scholarships and grants as you can. Choose a more affordable school. Work while you are in school, and keep expenses as low as possible. Try to ensure that your total student loan debt at graduation is less than your annual income."}
			]
		}
	},
	mounted() {
		document.body.className = 'white';
		setTimeout(() => {
			document.getElementById("sfc-main-content").classList.add("student-loan-calculator-content");
		}, 1000)
	},
	methods: {
		recalculate($event){
			let calc = new LoanCalculator({
				loanBalance: this.input.loan_amount,
				annualInterestRate: this.input.avg_interest/100.0,
				repaymentTermInYears: this.input.loan_term,
				fees: this.input.fees_percentage==null?0:this.input.fees_percentage/100,
				payFeesFromDisbursement:this.input.question_1=="Yes"?true:false,
				minimumPayment: this.input.minMonthlyPayment
			})
			this.results = calc.calculate().output;

			this.monthlyPayment = this.results.monthlyPayment.toFixed(2);
			this.numberOfPayments = this.results.numberOfPayments;
			this.totalPayments = this.results.totalPayments.toFixed(2);
			this.totalInterestPaid = this.results.totalInterestPaid.toFixed(2);
			this.feesAmount = this.results.feesAmount.toFixed(2);
			this.totalInterestPaidWithFees = this.results.totalInterestPaidWithFees.toFixed(2);
			this.resultDisplayed = true;

			let numberOfYears = Math.trunc(this.numberOfPayments/12);
			let numberOfMonths = this.numberOfPayments%12;

			this.durationString="";
			if(numberOfYears==1)
				this.durationString += numberOfYears + " year";
			else if(numberOfYears>1)
				this.durationString += numberOfYears + " years";

			if(numberOfYears>0 && numberOfMonths>0)
				this.durationString += " ";

			if(numberOfMonths==1)
				this.durationString += numberOfMonths + " month";
			else if(numberOfMonths>1)
				this.durationString += numberOfMonths + " months";

			setTimeout(() => {
				VueScrollTo.scrollTo('#res');
			}, 200)
			$event.preventDefault();
		},
		formatNumber(number)
		{
			number = Number(number).toLocaleString();

			// return number;

			let string = number.split('.');
			if(string[1]!=undefined)
			{
				string = string[1];

				if(string.length==1)
					return number+"0";
				else
					return number;
			}
			else
			{
				return number+".00";
			}

		},
		toggleAccordion(section){
			if (this.lastSectionOpened == section) {
				let input = document.getElementById(section)
				if (input.checked) {
					input.checked = false
				}
				this.lastSectionOpened = ""
			} else {
				this.lastSectionOpened = section
			}
		},
        create_schema(FAQList) {
            let jsonld = '{ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [';
            for (var i = 0; i < FAQList.length; i++) {
                if((i + 1) == FAQList.length){
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '" }}';
                }else{
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '"}},';
                }
            }
            jsonld += ']}';
            return jsonld;
        }
	},
	head(){
		return {
			title: "Loan Calculator",
			meta: [
				{hid: "ogmetatitle", property: 'og:title', content: "Loan Calculator"},
				{hid: "metadescription", name: 'description', content: "Calculate the monthly loan payment and total payments for federal and private student loans based on the loan balance, interest rate and repayment term."},
				{hid: "ogmetadescription", property: 'og:description', content: "Calculate the monthly loan payment and total payments for federal and private student loans based on the loan balance, interest rate and repayment term."},
				{
					hid: 'og-url',
					name: 'og:url',
					property: 'og:url',
					content: `${this.$store.state.config.sfcHost}/calculators/loan-calculator`
				},
				{
					hid: 'og-image',
					name: 'og:image',
					property: 'og:image',
					content: 'https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg'
				},
			],
			link: [
 					{ hid: "canonical_url", rel: "canonical", href: "https://www.savingforcollege.com/calculators/loan-calculator"}
 				],
			script: [
				{ type: 'text/javascript', src: 'https://s.ntv.io/serve/load.js', async: true, defer: true}
			],
			__dangerouslyDisableSanitizers: ['title', 'meta']
		}
	},
	filters: {
		capText(value){
			if(value.length < 165) {
				return value
			}
			let paragraph = ""
			let i = 0
			const words = value.split(" ")
			while (paragraph.length < 165 && (paragraph.length + words[i].length < 165)) {
				paragraph += ` ${words[i]}`
				i++
			}
			return paragraph.trim() + "..."
		}
	}

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* I have included page specific styles to the card here, widths and classes specifically for this calculator the rest of the main styles can be found in the "styles.css" */
	body
	{
		background: #fff;
	}
	.sfc-card {
		width: 40%;
		margin-top: 40px;
		margin-left: 28%;
	}
	.sfc-card .form {
		width: 90%;
		margin: 0px 4%;
	}
	.sfc-card.completed {
		width: 80%;
		margin-left: 5%;
	}
	.sfc-card.completed h5 {
		width: 50%;
	}
	.sfc-card.completed .form {
		width: 50%;
	}
	.results {
		width: 50%;
		border-radius: 0px 8px 8px 0px;
	}
	.next-steps {
		color: black;
	}
	@media only screen and (max-width: 1220px) {
		.sfc-card {
			width: 50%;
			margin-top: 40px;
			margin-left: 25%;
		}
		.sfc-card.completed {
			width: 100%;
			margin-left: 0%;
		}
	}

	@media only screen and (max-width: 768px) {
		.sfc-card {
			width: 100%;
			margin-top: 40px;
			margin-left: 0%;
			display: inline-block;
		}
		.sfc-card.completed .form {
			width: 96%;
		}
		.sfc-card .form {
			width: 96%;
			margin: 0px 2%;
		}
		.bluebg {
			background: #125393;
			color: #fff;
			border-radius: 0px;
		}
		.results {
			width: 100%;
			border-radius: 0px 0px 8px 8px;
		}
	}
</style>
